/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, window */

import { loadTenantConfig } from "./config-loader.js";
import { setupIdentityGate } from "./identity-gate.js";

// email-context protocol (DEV-3610, from flows-ui): no iframe here — widget
// script runs in this same document, so window.parent === window and the
// origin check on both sides is same-origin trivially. No "Back" navigation —
// displayMessageForm only opens a new window (Office.js has no in-place
// navigation API) and, separately, flows-ui always starts a fresh chat
// session on every mount regardless of thread_id, so it wouldn't restore
// the prior conversation anyway. Dropped rather than ship a button that
// looks like it resumes something but doesn't.
function postEmailContext(item) {
  const payload = {
    type: "update:rshp:email-context",
    context_id: "outlook",
    context: {
      thread_id: item == null ? undefined : item.conversationId || item.itemId,
      subject: item == null ? undefined : item.subject,
    },
  };
  window.postMessage(payload, window.location.origin);
}

function showConfigError(message) {
  document.getElementById("loading").style.display = "none";
  const errorBox = document.getElementById("config-error");
  errorBox.textContent = message;
  errorBox.style.display = "flex";
}

// Outlook can fire Office.onReady more than once per load; guard against
// double-registering the identity delegate and double-mounting the widget.
let initialized = false;

Office.onReady(async (info) => {
  if (info.host !== Office.HostType.Outlook || initialized) return;
  initialized = true;

  let config;
  try {
    config = await loadTenantConfig(window.location.search);
  } catch {
    initialized = false;
    showConfigError("This add-in isn't configured correctly. Contact your administrator.");
    return;
  }

  document.getElementById("loading").style.display = "none";
  const appBody = document.getElementById("app-body");
  appBody.style.display = "flex";

  // Must run before the widget/loader script below — flows-ui checks
  // window.__reshapeGetIdentityToken at mount time (DEV-3362). No-op when
  // this tenant's config has no identity block.
  await setupIdentityGate(config.identity);

  window.addEventListener("message", (e) => {
    if (e.origin !== window.location.origin) return;
    if (e.data?.type === "request:rshp:email-context") {
      postEmailContext(Office.context.mailbox.item);
    }
  });

  Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
    postEmailContext(Office.context.mailbox.item);
  });

  // Widget is only ever loaded here, after confirming we're actually inside
  // Outlook — not as a static <script> tag, which would load unconditionally
  // even if this page were opened directly in a plain browser tab. Matches
  // the production embed pattern (Cloudinary-hosted loader, classic deferred
  // script — not type="module" like the earlier dev-tunnel version).
  const widget = document.createElement("reshape-chat");
  widget.setAttribute("api-url", config.apiUrl);
  widget.setAttribute("deployment-id", config.deploymentId);
  widget.setAttribute("embedded", "");
  appBody.appendChild(widget);

  const script = document.createElement("script");
  script.src = "https://res.cloudinary.com/reshape-prod/raw/upload/ui/flows-ui.loader.js";
  script.dataset.deploymentId = config.deploymentId;
  script.defer = true;
  appBody.appendChild(script);

  postEmailContext(Office.context.mailbox.item);
});
